import { useState, useEffect, useCallback } from "react"
import {
  CreditCard,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Search,
  TrendingUp,
} from "lucide-react"
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import { getRevenueCatOverview, type RcOverview, type RcPurchaser } from "@/lib/api"
import { cn } from "@/lib/utils"

// Business-facing labels, not RC field names: entitlement→access, sandbox→test purchase
// (excluded from revenue), customer record→device record, identified→linked account,
// plan NULL→unrecognised purchase. Backend-only context stays under Diagnostics.

const PLAN_PRICE: Record<string, string> = {
  weekly: "$3.99 / week",
  monthly: "$9.99 / month",
  yearly: "$99.99 / year",
  annual: "$99.99 / year",
}

function shortProduct(productId: string): string {
  return productId.replace(/^com\.offseason\.wellness\.premium\./, "")
}

function formatDate(iso: string | null): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function tenure(iso: string | null): string {
  if (!iso) return "—"
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days < 0) return "—"
  if (days < 14) return `${days}d`
  if (days < 60) return `${Math.floor(days / 7)}w`
  return `${Math.floor(days / 30)}mo`
}

function money(n: number, decimals = 2): string {
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

const STATE_STYLE: Record<
  RcPurchaser["state"],
  { label: string; className: string; note?: string }
> = {
  active: {
    label: "Paying",
    className:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  },
  cancelling: {
    label: "Cancelling",
    className: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
    note: "Turned off auto-renew — still has access until the date shown",
  },
  billing_issue: {
    label: "Payment failed",
    className: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400",
    note: "Card declined, in grace period",
  },
  trialing: {
    label: "On free trial",
    className: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
    note: "Apple hasn't charged them yet — worth $0 until it converts",
  },
  churned: {
    label: "Left",
    className: "bg-muted text-muted-foreground",
    note: "Paid, then stopped",
  },
  trial_expired: {
    label: "Trial ended",
    className: "bg-muted text-muted-foreground",
    note: "Never converted — never paid us",
  },
}

/** Always rendered — a hidden tile that appears later is worse than one that explains why it's empty. */
function LtvTile({ ltv }: { ltv: RcOverview["ltv"] }) {
  if (ltv.available && ltv.usd != null) {
    return (
      <StatTile
        label="LTV per user"
        value={money(ltv.usd, 0)}
        tone="good"
        hint={
          `${money(ltv.revenue_per_year_usd ?? 0, 0)}/year × ${ltv.avg_retention_years} years retention` +
          (ltv.net_usd != null ? ` · ${money(ltv.net_usd, 0)} net of the store's cut` : "") +
          ` · from ${ltv.churn_events} departures over ${ltv.exposure_months} user-months`
        }
      />
    )
  }
  const waiting =
    ltv.blocked_reason === "no_churn_yet"
      ? "No paying user has left yet, so there is no retention figure to multiply by."
      : ltv.blocked_reason === "too_few_churn_events"
        ? `Only ${ltv.churn_events} of ${ltv.churn_events_required} departures needed — one or two would swing this wildly.`
        : "Not enough data yet."
  return (
    <StatTile
      label="LTV per user"
      value="Not yet"
      hint={`${waiting} Tracking ${ltv.exposure_months} user-months; fills in automatically at ${ltv.churn_events_required} departures.`}
    />
  )
}

function StatTile({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string
  value: number | string
  hint?: string
  tone?: "neutral" | "good" | "warn" | "bad"
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-2xl font-semibold tabular-nums",
          tone === "good" && "text-emerald-600 dark:text-emerald-400",
          tone === "warn" && "text-amber-600 dark:text-amber-400",
          tone === "bad" && "text-red-600 dark:text-red-400",
        )}
      >
        {value}
      </div>
      {hint ? (
        <div className="mt-1 text-xs leading-snug text-muted-foreground">{hint}</div>
      ) : null}
    </div>
  )
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {children}
    </div>
  )
}

const tooltipStyle = {
  fontSize: 12,
  borderRadius: 8,
  border: "1px solid hsl(var(--border))",
  background: "hsl(var(--card))",
  color: "hsl(var(--foreground))",
}

function fmtWeek(iso: string): string {
  const [, m, d] = iso.split("-")
  return `${Number(m)}/${Number(d)}`
}

/** Charts are context, not the source of truth — if RC's chart API is down the
 *  roster below is still correct, so say so rather than showing an empty box. */
function TrendCharts({ trend }: { trend: RcOverview["trend"] }) {
  const hasData = trend.mrr.length > 0 || trend.actives.length > 0
  if (!hasData) {
    return (
      <div className="rounded-lg border border-dashed bg-card p-4 text-sm text-muted-foreground">
        Trend charts unavailable right now
        {trend.error ? ` (${trend.error})` : ""}. The figures and roster below are
        unaffected — they come from a different RevenueCat endpoint.
      </div>
    )
  }
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <ChartCard
        title="Monthly recurring revenue"
        subtitle="Weekly, from RevenueCat's own MRR calculation. Last point is the current partial week."
      >
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={trend.mrr}
            margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="hsl(var(--border))"
            />
            <XAxis
              dataKey="week"
              tickFormatter={fmtWeek}
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
              minTickGap={24}
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis
              tick={{ fontSize: 10 }}
              stroke="hsl(var(--muted-foreground))"
              tickFormatter={(v: number) => `$${v}`}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(v) => [money(Number(v)), "MRR"]}
              labelFormatter={(l) => `Week of ${l}`}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#10b981"
              fill="#10b981"
              fillOpacity={0.18}
              strokeWidth={2}
              name="MRR"
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Paying subscriptions"
        subtitle="How many subscriptions are active each week. Counts subscriptions, so one user on two plans counts twice."
      >
        <ResponsiveContainer width="100%" height={200}>
          <LineChart
            data={trend.actives}
            margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="hsl(var(--border))"
            />
            <XAxis
              dataKey="week"
              tickFormatter={fmtWeek}
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
              minTickGap={24}
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 10 }}
              stroke="hsl(var(--muted-foreground))"
            />
            <Tooltip
              contentStyle={tooltipStyle}
              labelFormatter={(l) => `Week of ${l}`}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#6366f1"
              strokeWidth={2}
              dot={false}
              name="Subscriptions"
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  )
}

function MemberRow({ p }: { p: RcPurchaser }) {
  const style = STATE_STYLE[p.state]
  // Show what they're on NOW, so the plan matches the price beside it. Listing
  // every product they ever held made a $9.99 member look like $9.99 + $99.99.
  // Falls back to all products for trial/lapsed rows, which have nothing live.
  const shown = p.paid_products?.length
    ? p.paid_products
    : p.trial_products?.length
      ? p.trial_products
      : p.products
  const plans = shown.map(shortProduct)
  return (
    <tr className="border-b last:border-0 align-top hover:bg-muted/40">
      <td className="px-3 py-2.5 font-medium">
        {p.name ?? (
          <span className="italic text-muted-foreground">
            {p.has_profile ? "no name on profile" : "not in this database"}
          </span>
        )}
        {p.is_internal ? (
          <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
            test
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-col gap-0.5">
          {plans.map((plan) => (
            <span key={plan} className="text-xs">
              {PLAN_PRICE[plan] ?? plan}
            </span>
          ))}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">
        {p.monthly_value_usd > 0 ? (
          <span className="font-medium">{money(p.monthly_value_usd)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs">
        <div>{formatDate(p.started_paying_at)}</div>
        {p.started_paying_at ? (
          <div className="text-[11px] text-muted-foreground">
            {/* Never-paid rows share this column, so name what the date actually
                is rather than letting the "Paying since" header imply payment. */}
            {p.ever_paid ? `${tenure(p.started_paying_at)} ago` : "trial started"}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2.5">
        <span
          className={cn(
            "inline-block rounded px-1.5 py-0.5 text-[11px] font-medium",
            style.className,
          )}
        >
          {style.label}
        </span>
        {style.note ? (
          <div className="mt-0.5 text-[11px] leading-tight text-muted-foreground">
            {style.note}
          </div>
        ) : null}
        {p.entitlement_mismatch ? (
          <div className="mt-0.5 text-[11px] font-medium leading-tight text-red-600 dark:text-red-400">
            Paid, but our app may lock them out
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground">
        {formatDate(p.expires_at)}
      </td>
    </tr>
  )
}

function MemberTable({ rows }: { rows: RcPurchaser[] }) {
  if (!rows.length) {
    return (
      <div className="px-3 py-6 text-sm text-muted-foreground">Nothing here.</div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-medium">User</th>
            <th className="px-3 py-2 font-medium">Plan</th>
            <th className="px-3 py-2 text-right font-medium">Per month</th>
            <th className="px-3 py-2 font-medium">Paying since</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Paid through</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <MemberRow key={p.id} p={p} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Everything that needs backend context to read. Collapsed by default so the
 *  revenue view above stays answerable without explaining what a sandbox is. */
function Diagnostics({ data }: { data: RcOverview }) {
  const [open, setOpen] = useState(false)
  const c = data.counts
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <div className="rounded-lg border bg-card">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium hover:bg-muted/40"
      >
        <Chevron className="h-4 w-4" />
        Data quality &amp; diagnostics
        <span className="ml-auto text-xs font-normal text-muted-foreground">
          {c.entitlement_mismatches + c.plan_unmapped > 0
            ? `${c.entitlement_mismatches + c.plan_unmapped} needing attention`
            : "nothing needing attention"}
        </span>
      </button>
      {open ? (
        <div className="space-y-4 border-t px-4 py-4">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium">
                Paid but might be locked out — {c.entitlement_mismatches}
              </dt>
              <dd className="text-xs text-muted-foreground">
                RevenueCat took their money, but our own database doesn't have them
                marked as a subscriber. A safety net catches this at login, so they
                aren't actually locked out — but any number above zero means the two
                systems disagree about a paying customer, which is worth fixing.
                {c.purchasers_without_profile > 0 ? (
                  <>
                    {" "}
                    <span className="text-amber-600 dark:text-amber-400">
                      Can't be judged from here: {c.purchasers_without_profile} payers
                      have no record in the database this backend is pointed at
                      (staging). Point it at production to read this column.
                    </span>
                  </>
                ) : null}
              </dd>
            </div>
            <div>
              <dt className="font-medium">
                Purchase we didn't recognise — {c.plan_unmapped}
              </dt>
              <dd className="text-xs text-muted-foreground">
                Someone bought a product our backend has no entry for, so we stored
                their plan as blank. This is exactly the bug that hid real revenue
                for a month: the money arrived, but nothing recorded which plan it
                was. Above zero means a new or renamed App Store product needs
                adding.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Test purchases — {c.sandbox_only_purchasers}</dt>
              <dd className="text-xs text-muted-foreground">
                Purchases made through Apple's sandbox, which uses fake money — our
                own QA runs and App Review's checks. Real to RevenueCat, worth $0 to
                us, so they're excluded from every figure above and listed separately
                below.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Test accounts excluded — {c.internal_members_excluded}</dt>
              <dd className="text-xs text-muted-foreground">
                QA and dry-run subscriptions, left out of revenue. The team's own
                accounts are NOT on this list — if one of us actually paid, that's
                real money and it counts. The list holds{" "}
                {c.internal_allowlist_size} account
                {c.internal_allowlist_size === 1 ? "" : "s"} — if that looks too
                small, revenue above is overstating the business.
              </dd>
            </div>
          </dl>

          <div className="border-t pt-4">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Device records in RevenueCat (not people)
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatTile label="Total records" value={c.total_customers} />
              <StatTile
                label="Never linked to an account"
                value={c.anonymous}
                tone="warn"
              />
              <StatTile label="Linked to a real account" value={c.identified} />
              <StatTile label="Records we checked" value={c.probed} />
            </div>
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <div className="mb-1 font-semibold">
                The {c.anonymous.toLocaleString()} unlinked records are not{" "}
                {c.anonymous.toLocaleString()} people.
              </div>
              The mobile app calls <code>Purchases.logOut()</code> while login is
              still resolving on every cold start. RevenueCat mints a fresh
              anonymous record each time and logging back in doesn't merge it, so
              every app launch strands one throwaway. Cross-checked against PostHog:
              239 real installs produced ~1,400 records — they track app{" "}
              <em>opens</em>, not people. The mobile fix is merged but not yet
              shipped in a released build, so this number is still climbing by about
              20/day. None of it touches revenue: a record with no purchase is
              invisible to every figure above.
            </div>
          </div>

          {data.sandbox_purchasers.length ? (
            <div className="border-t pt-4">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Test purchases — excluded from revenue
              </div>
              <MemberTable rows={data.sandbox_purchasers} />
            </div>
          ) : null}

          <div className="border-t pt-3 text-xs text-muted-foreground">
            Project <code>{data.project_id}</code> · scope{" "}
            <code>{data.scope}</code> · {data.counts.probed} records checked in{" "}
            {data.elapsed_seconds}s{data.cached ? " · served from cache" : ""}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function RevenueCatPage() {
  const [data, setData] = useState<RcOverview | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState("")

  const load = useCallback(async (opts?: { refresh?: boolean }) => {
    setIsLoading(true)
    setError(null)
    try {
      setData(await getRevenueCatOverview({ refresh: opts?.refresh }))
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const c = data?.counts
  const q = search.trim().toLowerCase()
  const rows = (data?.real_purchasers ?? []).filter(
    (p) =>
      !q ||
      (p.name ?? "").toLowerCase().includes(q) ||
      p.products.some((prod) => prod.toLowerCase().includes(q)),
  )
  // Money first, departures second — the two questions asked of this page.
  // Three groups, because a lost payer and a trial that never converted are
  // different business events and must not share a table.
  const paying = rows.filter(
    (p) => !["churned", "trial_expired"].includes(p.state),
  )
  const gone = rows.filter((p) => p.state === "churned")
  const trialsEnded = rows.filter((p) => p.state === "trial_expired")
  // A test account showing "Cancelling" while the at-risk tile reads $0 looks like
  // a contradiction. It isn't — QA subscriptions are left out of every figure — but
  // the page should say so rather than leave the reader to reconcile it.
  const leavingInternalOnly = (data?.real_purchasers ?? []).some(
    (p) => p.is_internal && (p.state === "cancelling" || p.state === "billing_issue"),
  )

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <CreditCard className="h-6 w-6" />
            Revenue
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Who pays us, how much, since when, and who is leaving. Figures exclude
            QA accounts and Apple's test purchases, so they reflect the real
            business. A teammate who actually paid counts like anyone else.
          </p>
        </div>
        <button
          onClick={() => void load({ refresh: true })}
          disabled={isLoading}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">Couldn't load revenue data</div>
            <div className="mt-0.5 text-xs">{error}</div>
          </div>
        </div>
      ) : null}

      {isLoading && !data ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          Reading RevenueCat — this takes a few seconds…
        </div>
      ) : null}

      {data && c ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatTile
              label="Monthly revenue"
              value={money(c.mrr_usd)}
              tone="good"
              hint={`${money(c.arr_usd, 0)} a year at this rate · list prices, before the store's cut (measured at 30% — see collected)`}
            />
            <StatTile
              label="Paying users"
              value={c.live_paying_members}
              tone="good"
              hint={
                c.internal_members_excluded
                  ? `${c.internal_members_excluded} test account${c.internal_members_excluded === 1 ? "" : "s"} excluded`
                  : "Everyone with active paid access"
              }
            />
            <StatTile
              label="Revenue at risk"
              value={money(c.mrr_at_risk_usd)}
              tone={c.mrr_at_risk_usd > 0 ? "warn" : "good"}
              hint={
                c.mrr_at_risk_usd > 0
                  ? `${c.cancelling} cancelling, ${c.billing_issues} payment failure${c.billing_issues === 1 ? "" : "s"} — still counted above until they lapse`
                  : leavingInternalOnly
                    ? "No paying user is leaving. A test account below shows as cancelling; QA subscriptions are excluded from every figure here."
                    : "Nobody has cancelled or had a payment fail"
              }
            />
            <StatTile
              label="Collected to date"
              value={money(c.realized_gross_usd, 0)}
              tone="good"
              hint={`${money(c.realized_proceeds_usd, 0)} after the store's cut · exact amounts billed, the only figure here that isn't a list price`}
            />
            <StatTile
              label="Revenue per user"
              value={
                c.revenue_per_member_usd != null
                  ? money(c.revenue_per_member_usd)
                  : "—"
              }
              hint={
                c.revenue_per_member_usd != null
                  ? `${money(c.realized_gross_usd, 0)} ÷ ${c.ever_paid_members} who ever paid · TO DATE, not lifetime — it climbs on its own as users keep renewing. True LTV needs a churn rate, and nobody has churned yet.`
                  : "Nobody has paid yet"
              }
            />
            <LtvTile ltv={data.ltv} />
            <StatTile
              label="Churn"
              value={
                c.paid_churn_rate_pct != null ? `${c.paid_churn_rate_pct}%` : "—"
              }
              tone={
                c.paid_churn_rate_pct == null
                  ? "neutral"
                  : c.paid_churn_rate_pct > 0
                    ? "warn"
                    : "good"
              }
              hint={
                c.paid_churn_rate_pct == null
                  ? "Nobody has paid yet, so there is no rate to report"
                  : `${c.churned} of ${c.ever_paid_members} users who ever paid have left` +
                    // The monthly rate is the figure LTV divides by; showing both
                    // stops the two tiles reading as unrelated facts.
                    (data.ltv.monthly_churn_rate_pct
                      ? ` · ${data.ltv.monthly_churn_rate_pct}%/month, which is what LTV divides by`
                      : "")
              }
            />
            <StatTile
              label="Free trials"
              value={c.trialing}
              hint={
                c.trial_expired
                  ? `${c.trial_expired} ended without converting — they never paid, so they are not counted as churn`
                  : "Currently on trial · worth $0 until they convert"
              }
            />
          </div>

          <TrendCharts trend={data.trend} />

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name or plan…"
              className="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm"
            />
          </div>

          <div className="rounded-lg border bg-card">
            <div className="flex items-center gap-2 border-b px-4 py-3 text-sm font-medium">
              <TrendingUp className="h-4 w-4" />
              Current users ({paying.length})
            </div>
            <MemberTable rows={paying} />
          </div>

          {gone.length ? (
            <div className="rounded-lg border bg-card">
              <div className="border-b px-4 py-3 text-sm font-medium">
                Former paying users ({gone.length})
              </div>
              <MemberTable rows={gone} />
            </div>
          ) : null}

          {trialsEnded.length ? (
            <div className="rounded-lg border bg-card">
              <div className="border-b px-4 py-3">
                <div className="text-sm font-medium">
                  Trials that didn't convert ({trialsEnded.length})
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Signed up for a free trial and let it lapse. They never paid, so
                  they are a conversion problem rather than churn — worth acting on,
                  but not a sign we are losing customers.
                </div>
              </div>
              <MemberTable rows={trialsEnded} />
            </div>
          ) : null}

          <Diagnostics data={data} />
        </>
      ) : null}
    </div>
  )
}
